import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { DEFAULT_ORDER_STATUS } from '../../../helpers/constants';
import { isStaff, scrollToId } from '../../../helpers/global';
import { requestedByApplies, requestedByError } from '../../../helpers/requestedBy';
import useAppChoices from '../../../hooks/useAppChoices';
import { SubmitButton } from '../../services/pipeline/Form/Common/SubmitButton';
import { Estoppel } from './OrderTypeForms/Estoppel';
import { LienSearch } from './OrderTypeForms/LienSearch';
import './OrderTypeForms/styles.scss';
import { Survey } from './OrderTypeForms/Survey';
import { ServiceForm } from './ServiceForm';
import { validateEmail } from '../../../helpers/global';
import { blankHoaSnapshot } from '../../HoaDirectory/HoaDirectoryHelper';
import { serializeClientRequestHoaInfo } from '../../hoaForm/hoaFormData';

const InitialLienFormFieldError = { isAssignedProcessorsNotValid: false, isStatusNotValid: false };

const getDefaultProcessingFee = (orgs = [], selectedOrgId, processingFeeKey) => {
  if (!selectedOrgId) return '';

  const organization = orgs.find(o => o._id === selectedOrgId);
  if (!organization) return '';

  return organization[processingFeeKey] || '';
};

const OrderDetails = ({
  handleBack,
  defaultActiveOrderType,
  isInternal,
  handleOrderDetailsSubmit,
  status,
  orderDetails
}) => {
  const role = useMemo(() => localStorage.getItem('user-role'), []);
  const orgs = useAppChoices('organizations');
  const users = useAppChoices('users');
  const estoppelStatuses = useAppChoices('Estoppel Status');
  const surveyStatuses = useAppChoices('Survey Status');
  const surveyor = useAppChoices('Surveyor');
  const underwriterCertification = [...useAppChoices('Underwriter Certification'), 'Others'];
  const lienSearchStatuses = useAppChoices('Lien Search Status');

  // Service form
  const [formData, setFormData] = useState({
    isIndividualService: false,
    individual: {
      name: '',
      email: '',
      phoneNumber: ''
    },
    fileName: '',
    folioNumbers: '',
    closingDate: '',
    propertyAddress: '',
    city: '',
    state: 'FLORIDA',
    zipCode: '',
    buyerName: '',
    buyerPhone: '',
    buyerEmail: '',
    sellerName: '',
    sellerEmail: '',
    sellerPhone: '',
    modificationAndSpecialInstructions: '',
    legalDescription: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // Lien form
  const [lienFormData, setLienFormData] = useState(
    isStaff(role)
      ? {
        status: DEFAULT_ORDER_STATUS,
        assignedProcessors: [],
        estimatedTurnAroundTime: '',
        hardCost: '',
        rush: false,
        processingFee: getDefaultProcessingFee(orgs, formData.organization, 'lienSearchProcessingFee')
      }
      : { rush: false, processingFee: '' }
  );
  const [lienFormFieldError, setLientFormFieldError] = useState(isStaff(role) ? InitialLienFormFieldError : {});

  // Estoppel form
  const [estoppelFormData, setEstoppelFormData] = useState(
    !isStaff(role)
      ? {
        homeOwnerInformation: [blankHoaSnapshot()],
        rush: false
      }
      : {
        status: DEFAULT_ORDER_STATUS,
        assignedProcessors: [],
        estimatedTurnAroundTime: '',
        hardCost: '',
        rush: false,
        processingFee: getDefaultProcessingFee(orgs, formData.organization, 'estoppelProcessingFee'),
        homeOwnerInformation: [blankHoaSnapshot()]
      }
  );
  const [estoppelFormErrors, setEstoppelFormErrors] = useState({});
  const [numberOfHOA, setNumberOfHOA] = useState(1);

  // Survey form
  const [surveyFormData, setSurveyFormData] = useState(
    isStaff(role)
      ? {
        status: DEFAULT_ORDER_STATUS,
        assignedProcessors: [],
        listingAgentName: '',
        listingAgentPhone: '',
        hardCopy: 'no',
        titleCertification: '',
        buyerCertification: '',
        lenderCertification: '',
        underwriterCertification: '',
        rush: false,
        neededDate: '',
        files: []
      }
      : {
        hardCopy: 'no',
        titleCertification: '',
        buyerCertification: '',
        lenderCertification: '',
        underwriterCertification: '',
        rush: false,
        neededDate: ''
      }
  );
  const [surveyFormErrors, setSurveyFormErrors] = useState({});

  const showLienForm = defaultActiveOrderType.includes('lienSearch');
  const showEstoppelForm = defaultActiveOrderType.includes('estoppel');
  const showSurveyForm = defaultActiveOrderType.includes('survey');

  const validateServiceForm = () => {
    const errors = {};

    const requiredFields = [
      'isIndividualService',
      'fileName',
      'folioNumbers',
      'closingDate',
      'propertyAddress',
      'city',
      'state',
      'zipCode',
      'buyerName',
      'sellerName'
    ];

    errors.individual = {};
    requiredFields.forEach(field => {
      if (field === 'isIndividualService' && formData[field] === true) {
        const requiredInvidualForm = ['name', 'email'];
        requiredInvidualForm.forEach(field => {
          if (!formData.individual[field].trim()) {
            errors.individual[field] = 'This field is required.';
          } else if (field === 'email') {
            const isValid = validateEmail(formData.individual[field]);
            if (!isValid) {
              errors.individual[field] = 'Email is not valid.';
            }
          }
        });
      } else if (field === 'fileName' && formData[field].length > 250) {
        errors[field] = 'File name cannot exceed 250 characters.';
      } else if (field !== 'isIndividualService' && !formData[field].trim()) {
        errors[field] = 'This field is required.';
      }
    });

    if (Object.keys(errors.individual).length === 0) {
      delete errors.individual;
    }

    if (isStaff(role) && !formData.isIndividualService) {
      const organization = orgs.find(org => org._id === formData.organization);
      const requestedByMessage = requestedByError({
        organization,
        isIndividual: false,
        users,
        requestedBy: formData.requestedBy
      });
      if (requestedByMessage) errors.requestedBy = requestedByMessage;
    }

    setFormErrors(errors);
    const allErrorKeys = Object.keys(errors);
    if (allErrorKeys.length > 0) scrollToId(allErrorKeys[0]);
    return allErrorKeys.length === 0;
  };

  const validateLienSearchForm = () => {
    if (!isStaff(role) || !showLienForm) return true;
    const errors = {};

    if (lienFormData?.assignedProcessors?.length <= 0) {
      setLientFormFieldError({ ...lienFormFieldError, isAssignedProcessorsNotValid: true });
      errors['isAssignedProcessorsNotValid'] = true;
      scrollToId('lienAssignedProcessors');
    }

    return Object.keys(errors).length === 0;
  };

  const validateEstoppelForm = () => {
    if (!showEstoppelForm) return true;
    const newErrors = {};

    if (isStaff(role)) {
      if (estoppelFormData?.assignedProcessors.length < 1)
        newErrors.estoppelAssignedProcessors = 'Assigned Processors is required.';
    }

    if ((estoppelFormData.homeOwnerInformation || []).length > 0) {
      (estoppelFormData.homeOwnerInformation || []).forEach((hoa, i) => {
        if (!String(hoa?.name || '').trim()) {
          newErrors[`HOA${i}Name`] = 'Name is required.';
          newErrors[`name-${i}`] = true;
        }
        if (
          isStaff(role) &&
          !hoa?.noEstoppelNeeded &&
          hoa?.managementType === 'managed' &&
          !String(hoa?.managementCompany || '').trim()
        ) {
          newErrors[`HOA${i}ManagementCompany`] = 'Company name is required when managed.';
          newErrors[`managementCompany-${i}`] = true;
        }
      });
    }

    setEstoppelFormErrors(newErrors);

    const allErrorKeys = Object.keys(newErrors);
    if (allErrorKeys.length > 0) scrollToId(allErrorKeys[0], 100);
    return allErrorKeys.length === 0;
  };

  const validateSurveyForm = () => {
    if (!showSurveyForm) return true;

    const newErrors = {};
    if (isStaff(role)) {
      if (surveyFormData.assignedProcessors.length < 1)
        newErrors.surveyAssignedProcessors = 'Assigned Processors is required.';
    }
    if (!surveyFormData.titleCertification) newErrors.titleCertification = 'Title Certification is required.';
    if (!surveyFormData.buyerCertification) newErrors.buyerCertification = 'Buyer Certification is required.';
    if (!surveyFormData.lenderCertification) newErrors.lenderCertification = 'Lender Certification is required.';
    if (!surveyFormData.underwriterCertification)
      newErrors.underwriterCertification = 'Underwriter Certification is required.';
    if (surveyFormData.underwriterCertification === 'Others' && !surveyFormData?.underwriterCertificationOther)
      newErrors.underwriterCertificationOther = 'Please specify other underwriter certification.';
    setSurveyFormErrors(newErrors);

    const allErrorKeys = Object.keys(newErrors);
    if (allErrorKeys.length > 0) scrollToId(allErrorKeys[0], 100);
    return allErrorKeys.length === 0;
  };

  const handleSubmit = e => {
    e.preventDefault();

    if (!validateServiceForm() || !validateLienSearchForm() || !validateEstoppelForm() || !validateSurveyForm()) {
      toast.error('Please fill all the required fields');
      return;
    }

    const orders = [];
    if (showLienForm) {
      let lienSearchFinalData = { orderType: 'Lien Search', ...lienFormData };
      if (isStaff(role)) {
        lienSearchFinalData = {
          ...lienSearchFinalData
        };
      }

      orders.push(lienSearchFinalData);
    }
    if (showEstoppelForm) {
      const { homeOwnerInformation, ...restOfEstoppel } = estoppelFormData;
      const estoppelFinalData = {
        ...restOfEstoppel,
        orderType: 'Estoppel',
        ...(isStaff(role)
          ? {
              homeOwnerInformation: (homeOwnerInformation || []).map(({ autoLinked, ...rest }) => rest),
            }
          : {
              requestHoaInfo: serializeClientRequestHoaInfo(homeOwnerInformation),
            }),
      };
      orders.push(estoppelFinalData);
    }
    if (showSurveyForm) {
      const { hardCopy, underwriterCertification, underwriterCertificationOther, ...restOfSurvey } = surveyFormData;
      const surveyFinalData = {
        ...restOfSurvey,
        orderType: 'Survey',
        hardCopy: hardCopy === 'yes',
        underwriterCertification:
          underwriterCertification === 'Others' ? underwriterCertificationOther : underwriterCertification
      };
      orders.push(surveyFinalData);
    }

    let finalFormData = { ...formData };

    if (formData.isIndividualService) {
      const { organization, ...formDataWithoutOrg } = finalFormData;
      finalFormData = {
        ...formDataWithoutOrg,
        individual: {
          name: formData.individual.name,
          email: formData.individual.email,
          phoneNumber: formData.individual.phoneNumber
        }
      };
    } else {
      const { individual, ...formDataWithoutIndividual } = finalFormData;
      finalFormData = {
        ...formDataWithoutIndividual,
        organization: formData.organization
      };
    }
    const organization = orgs.find(org => org._id === finalFormData.organization);
    if (!isStaff(role) || !requestedByApplies(organization, finalFormData.isIndividualService)) {
      delete finalFormData.requestedBy;
    }
    handleOrderDetailsSubmit({ ...finalFormData, orders });
  };

  const onOrganizationChange = orgId => {
    setEstoppelFormData({
      ...estoppelFormData,
      processingFee: getDefaultProcessingFee(orgs, orgId, 'estoppelProcessingFee')
    });
    setLienFormData({
      ...lienFormData,
      processingFee: getDefaultProcessingFee(orgs, orgId, 'lienSearchProcessingFee')
    });
    setSurveyFormData({
      ...surveyFormData,
      processingFee: getDefaultProcessingFee(orgs, orgId, 'surveyProcessingFee')
    });
  };

  // Filter objects based on orderType
  function filterByOrderType(orderType) {
    if (Object.keys(orderDetails).length < 1) return [];
    return orderDetails.orders.filter(item => item.orderType === orderType);
  }

  return (
    <>
      <div className="order-details">
        <p className="order-type-section-title">Order Details</p>
        <p className="order-type-section-subtitle">Please provide the required information for your order</p>
        <ServiceForm
          defaultValues={orderDetails}
          formData={formData}
          setFormData={setFormData}
          formErrors={formErrors}
          setFormErrors={setFormErrors}
          orgs={orgs}
          role={role}
          onOrganizationChange={onOrganizationChange}
        />
        {showLienForm && (
          <>
            <hr />
            <LienSearch
              role={role}
              lienSearchStatuses={lienSearchStatuses}
              defaultValues={filterByOrderType('Lien Search')?.[0] ?? {}}
              city={formData.city}
              formData={lienFormData}
              setFormData={setLienFormData}
              formFieldError={lienFormFieldError}
              setFormFieldError={setLientFormFieldError}
              initialFormFieldError={InitialLienFormFieldError}
            />
          </>
        )}

        {showEstoppelForm && (
          <>
            <hr />
            <Estoppel
              defaultValues={filterByOrderType('Estoppel')?.[0] ?? {}}
              errors={estoppelFormErrors}
              numberOfHOA={numberOfHOA}
              setNumberOfHOA={setNumberOfHOA}
              role={role}
              formData={estoppelFormData}
              setFormData={setEstoppelFormData}
              estoppelStatuses={estoppelStatuses}
            />
          </>
        )}

        {showSurveyForm && (
          <>
            <hr />
            <Survey
              role={role}
              surveyStatuses={surveyStatuses}
              surveyor={surveyor}
              underwriterCertification={underwriterCertification}
              defaultValues={filterByOrderType('Survey')?.[0] ?? {}}
              formData={surveyFormData}
              setFormData={setSurveyFormData}
              errors={surveyFormErrors}
            />
          </>
        )}
        <div className="empty-section" />
        <SubmitButton
          buttonText={isInternal ? 'Submit' : undefined}
          status={status}
          onCancel={handleBack}
          onSubmit={handleSubmit}
        />
      </div>
    </>
  );
};

export default OrderDetails;
